import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { loadChats } from '../lib/chats'

const ReadChatsContext = createContext(null)

export function ReadChatsProvider({ children }) {
  const [readChatIds, setReadChatIds] = useState(new Set())
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false)

  const markChatAsRead = useCallback((chatId) => {
    if (!chatId) return
    setReadChatIds((prev) => {
      const next = new Set(prev)
      next.add(chatId)
      return next
    })
  }, [])

  const isChatRead = useCallback((chatId) => readChatIds.has(chatId), [readChatIds])

  // Проверяем непрочитанные сообщения при инициализации и подписываемся на новые
  useEffect(() => {
    let channel = null

    const checkUnread = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id
      if (!userId) return

      // Проверяем непрочитанные при загрузке
      const chatsList = await loadChats(userId)
      const hasUnread = chatsList.some((c) => (c.unreadCount || 0) > 0)
      setHasUnreadMessages(hasUnread)

      // Подписываемся на новые сообщения во всех чатах пользователя
      channel = supabase
        .channel('read-chats-updates')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          async (payload) => {
            const newMsg = payload?.new
            if (!newMsg) return
            
            // Проверяем, относится ли сообщение к чатам пользователя
            const chatId = newMsg.chat_id
            const { data: chatData } = await supabase
              .from('chats')
              .select('user1_id, user2_id')
              .eq('id', chatId)
              .single()
            
            if (chatData && (chatData.user1_id === userId || chatData.user2_id === userId)) {
              // Если сообщение от другого пользователя - обновляем статус непрочитанных
              if (newMsg.sender_id !== userId) {
                const updatedChats = await loadChats(userId)
                const hasUnread = updatedChats.some((c) => (c.unreadCount || 0) > 0)
                setHasUnreadMessages(hasUnread)
              }
            }
          }
        )
        .subscribe()
    }

    checkUnread()

    return () => {
      if (channel) {
        supabase.removeChannel(channel)
      }
    }
  }, [])

  return (
    <ReadChatsContext.Provider value={{ markChatAsRead, isChatRead, readChatIds, hasUnreadMessages, setHasUnreadMessages }}>
      {children}
    </ReadChatsContext.Provider>
  )
}

export function useReadChats() {
  const ctx = useContext(ReadChatsContext)
  if (!ctx) {
    return {
      markChatAsRead: () => {},
      isChatRead: () => false,
      readChatIds: new Set(),
      hasUnreadMessages: false,
      setHasUnreadMessages: () => {}
    }
  }
  return ctx
}
